import * as React from 'react';
import { RepresentantModel, Representant, Person } from './org.admin.types';
import { renderFields, EMAIL_REGEX, validateFieldByRegex } from './org.admin.helpers';
import _ from 'lodash';
import { Dialog } from 'sos-common/js/components/Dialog/Dialog';
import { Icon } from 'sos-common/js/components/Icon/icon';
import { Field } from '../StatsbidragGlobals/statsbidrag.types';
import { FormField } from 'sos-common/js/commonTypes/formField.types';

export interface ListRepresentativesProps {
  canInviteRepresentant: boolean;
  canInviteSamordnare: boolean;
  representants: RepresentantModel[];
}

export interface ListRepresentativesState {
  enableSendButton: boolean;
  currentRepresentant: Representant;
  fields: Field[];
  fieldsSnapshot: Field[];
  isModalOpen: boolean;
  deleteId: string;
}

/**
 * Lists representatives for an organisation and gives ability to change the information.
 * Client side validation is available, see
 */
export class ListRepresentatives extends React.Component<ListRepresentativesProps, ListRepresentativesState> {
  constructor(props: ListRepresentativesProps) {
    super(props);
    this.state = {
      enableSendButton: false,
      currentRepresentant: null,
      fields: null,
      fieldsSnapshot: null,
      isModalOpen: false,
      deleteId: '',
    };
  }

  validateRadioField = (fieldCopy: Field[]) => {
    fieldCopy.forEach((field) => {
      if (field.type === 'radio') {
        field.isValid = true;
        field.isChecked = false;
      }
    });
  };

  areAllFieldsValid = (fields: Field[]) => {
    const textFieldsValid = fields.find((x) => x.type !== 'radio' && !x.isValid) === undefined;
    let radioIsValid = fields.find((x) => x.type === 'radio') === undefined;
    fields.forEach((field) => {
      if (field.type === 'radio' && field.isValid) {
        radioIsValid = true;
      }
    });
    return textFieldsValid && radioIsValid;
  };

  fieldChanged = (formfield: FormField<any>) => {
    const fieldCopy = this.state.fields.slice();
    let field = fieldCopy.find((x) => x.name === formfield.fieldName);

    if (formfield.fieldName === 'UppdateraForetradare.Roll') {
      field = fieldCopy.find((x) => x.name === formfield.fieldName && x.value === formfield.value);
    }

    field.value = formfield.value;

    if (field.type === 'radio') {
      this.validateRadioField(fieldCopy);
      field.isChecked = true;
    } else {
      if (field.mandatory && !field.value) {
        field.isValid = false;
        field.errorMessage = 'Denna uppgift är obligatorisk att ange';
      } else if (field.validation && field.value) {
        field.isValid = validateFieldByRegex(field.validation, field.value);

        if (!field.isValid && field.name === 'UppdateraForetradare.Epost') {
          field.errorMessage = 'Den angivna e-postadress stämmer inte med formatet abc@mail.se';
        }
      } else {
        field.isValid = true;
      }
    }

    const areAllFieldsValid = this.areAllFieldsValid(fieldCopy);
    const fieldsDifferFromSnapshot = !_.isEqual(fieldCopy, this.state.fieldsSnapshot);

    this.setState({
      fields: fieldCopy,
      enableSendButton: areAllFieldsValid && fieldsDifferFromSnapshot,
    });
  };

  /**
   * Creates necessary fields to be able to create & edit a person.
   */
  defineFields = (rep: RepresentantModel) => {
    let fields = [
      {
        id: 'id_UppdateraForetradare.Befattning_edit',
        type: 'text',
        label: 'Ändra befattning i organisationen',
        name: 'UppdateraForetradare.Befattning',
        placeholder: 'Tjänstetitel',
        validation: '',
        mandatory: true,
        value: rep.Foretradare.Befattning,
        isValid: true,
      },
      {
        id: 'id_UppdateraForetradare.Epost_edit',
        type: 'text',
        label: 'Ändra e-postadress',
        name: 'UppdateraForetradare.Epost',
        placeholder: 'abc@mail.se',
        validation: EMAIL_REGEX,
        mandatory: true,
        value: rep.Foretradare.Epost,
        isValid: true,
      },
      {
        id: 'id_SkapaForetradare.Telefon_edit',
        type: 'text',
        label: 'Ändra telefon',
        name: 'UppdateraForetradare.Telefon',
        placeholder: '08-987 65 43',
        validation: '',
        mandatory: true,
        value: rep.Foretradare.Telefon,
        isValid: true,
      },
    ] as Field[];

    if (!rep.CanUserChangePermissions) {
      fields.push({
        id: 'id_roll_edit',
        type: 'text',
        label: '',
        name: 'UppdateraForetradare.Roll',
        placeholder: '',
        validation: '',
        mandatory: false,
        value: rep.Foretradare.Roll,
        isValid: true,
        hidden: true,
      } as Field);
    }

    if (rep.CanUserChangePermissions && this.props.canInviteRepresentant) {
      fields.push({
        id: 'id_representant_edit',
        type: 'radio',
        label: 'Representant',
        name: 'UppdateraForetradare.Roll',
        placeholder: '',
        validation: '',
        mandatory: true,
        value: 'Representant',
        isValid: true,
        radioHeading: 'Ändra behörighet',
        isChecked: rep.Foretradare.Roll === 'Representant',
      } as Field);
    }

    if (rep.CanUserChangePermissions && this.props.canInviteSamordnare) {
      fields.push({
        id: 'id_uppgiftssamordnare_edit',
        type: 'radio',
        label: 'Uppgiftssamordnare',
        name: 'UppdateraForetradare.Roll',
        placeholder: '',
        validation: '',
        mandatory: true,
        value: 'Uppgiftssamordnare',
        isValid: true,
        radioHeading: 'Ändra behörighet',
        isChecked: rep.Foretradare.Roll === 'Uppgiftssamordnare',
      } as Field);
    }

    return fields;
  };

  clickEditRepresentative = (e: React.MouseEvent) => {
    e.preventDefault();
    const index = Number(e.currentTarget.getAttribute('data-index'));
    if (!isNaN(index)) {
      this.setState({
        currentRepresentant: this.props.representants[index].Foretradare,
        fields: this.defineFields(this.props.representants[index]),
        fieldsSnapshot: this.defineFields(this.props.representants[index]),
        enableSendButton: false,
      });
    }
  };

  renderEditView = (rep: Representant) => {
    const { currentRepresentant, enableSendButton } = this.state;
    return (
      <React.Fragment>
        {currentRepresentant && currentRepresentant.Id === rep.Id && (
          <div className="orgadmin__repdetails">
            {renderFields(this.state.fields, this.fieldChanged, false)}

            <input className="button" type="submit" disabled={!enableSendButton} value="Uppdatera" />
          </div>
        )}
      </React.Fragment>
    );
  };

  abortEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    this.setState({
      currentRepresentant: null,
    });
  };

  deleteRepresentative = (e: React.MouseEvent) => {
    e.preventDefault();
    const index = Number(e.currentTarget.getAttribute('data-index'));
    if (!isNaN(index)) {
      const field = this.props.representants[index];
      const id = field.Foretradare.Id;
      this.setState({
        deleteId: id,
        isModalOpen: true,
      });
    }
  };

  renderListOfRepresentatives = (repModel: RepresentantModel, index: number) => {
    const { currentRepresentant } = this.state;
    const rep = repModel.Foretradare;
    const currRepId = currentRepresentant ? currentRepresentant.Id : null;
    return (
      <div key={index} className="orgadmin__replist">
        <div>
          <div className="orgadmin__repname">
            <span>
              {rep.Person.Fornamn} {rep.Person.Efternamn}
            </span>
          </div>
          <div className="orgadmin__repfooter">
            <div className="orgadmin__reprole">{rep.Roll}</div>
          </div>
        </div>
        <div className="orgadmin__repactions">
          {currentRepresentant && currentRepresentant.Id === rep.Id && (
            <button
              type="button"
              onClick={this.abortEdit}
              className="stripped-button orgadmin__repchangebtn orgadmin__repchangebtn--abort">
              Avbryt
            </button>
          )}
          {currRepId !== rep.Id && repModel.CanDelete && (
            <button
              type="button"
              onClick={this.deleteRepresentative}
              data-index={index}
              className="stripped-button orgadmin__repdeletebtn">
              <Icon isAriaHidden={true} iconType="fontawesome/trash-alt" className="orgadmin__repactions__trashicon" />
              Ta bort
            </button>
          )}
          {currRepId !== rep.Id && repModel.CanEdit && (
            <button
              type="button"
              onClick={this.clickEditRepresentative}
              data-index={index}
              className="stripped-button orgadmin__repchangebtn">
              Ändra
            </button>
          )}
        </div>
        {this.renderEditView(rep)}
      </div>
    );
  };

  closeConfirm = () => {
    this.setState({
      isModalOpen: false,
      enableSendButton: false,
    });
  };

  public render() {
    const { representants } = this.props;
    const { currentRepresentant } = this.state;
    const currentRepId = currentRepresentant ? currentRepresentant.Id : '';
    return (
      <div>
        <form action={`Edit/?id=${currentRepId}`} method="POST">
          {representants && representants.map(this.renderListOfRepresentatives)}
        </form>
        <Dialog isOpen={this.state.isModalOpen}>
          <button onClick={this.closeConfirm} className="close">
            <Icon isAriaHidden={false} ariaLabel="Stäng" iconType="ic_close_white_24px" />
          </button>

          <div className="header">
            <h2>Ta bort behörighet</h2>
          </div>
          <p>Är du säker på att du vill ta bort behörigheten? Det går inte att ångra.</p>
          <div className="footer">
            <button className="button button--white" onClick={this.closeConfirm}>
              Avbryt
            </button>
            <form action={`Delete/?id=${this.state.deleteId}`} method="POST">
              <button type="submit" className="button button--red">
                Ta bort
              </button>
            </form>
          </div>
        </Dialog>
      </div>
    );
  }
}
